import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  ChangeEvent,
  FC,
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';
import asyncActionSwal from 'utils/asyncActionSwal';
import cleanText from 'utils/cleanText';
import { getNewOperationDate } from 'utils/getNewOperationDate';

import { cleanValue } from 'constants/formats';
import operationTypes from 'constants/operationTypes';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';

import {
  Movement,
  Range,
  Transaction,
  HistoryMovement,
  SelectorOption,
} from 'types';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addDocument } from 'modules/documents/redux/documentActions';
import { getValidMovements } from 'modules/transactions/utils/getValidMovements';
import { useGetPartnerOptionsForOperationModal } from 'modules/transactions/hooks/useGetPartnerOptionsForOperationModal';
import { useGetHoldingClassOptionsForOperationModal } from 'modules/transactions/hooks/useGetHoldingClassOptionsForOperationModal';
import { useGetAvailableRanges } from 'modules/_shared/hooks/useGetAvailableRanges';
import { useGetLastOperationDate } from 'modules/transactions/hooks/useGetLastOperationDate';
import { addOperation } from 'modules/transactions/redux/operationActions';

import { getMovements } from '../utils/getMovements';
import { getHistoryMovements } from '../utils/getHistoryMovements';
import { getTransactions } from '../utils/getTransactions';
import { buildMockDataFromBaseData } from '../utils/buildMockDataFromBaseData';
import { baseDataForCapitalDecrease } from '../constants/baseDataForCapitalDecrease';

import { CapitalDecreaseModalContextType } from '../types/CapitalDecreaseModalContextType';
import { capitalDecreaseModalInitValue } from '../constants/capitalDecreaseModalInitValue';

const CapitalDecreaseModalContext =
  createContext<CapitalDecreaseModalContextType>(capitalDecreaseModalInitValue);

export const CapitalDecreaseModalProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [date, setDate] = useState<string>('');
  const [scriptureView, setScriptureView] = useState<boolean>(true);
  const [totalSharesToDecrease, setTotalSharesToDecrease] = useState<number>(0);
  const [comments, setComments] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [notaryName, setNotaryName] = useState<string>('');
  const [notaryProtocol, setNotaryProtocol] = useState<string>('');
  const [notaryCity, setNotaryCity] = useState<string>('');
  const [remainingShares, setRemainingShares] = useState<number>(0);
  const [unsavedShares, setUnsavedShares] = useState<number>(0);
  const [selectedPartner, setSelectedPartner] = useState<string>('');
  const [sharesToDecrease, setSharesToDecrease] = useState<number>(0);
  const [shareFrom, setShareFrom] = useState<number>(0);
  const [shareTo, setShareTo] = useState<number>(0);
  const [hasCustomSharePrice, setHasCustomSharePrice] =
    useState<boolean>(false);
  const [sharePrice, setSharePrice] = useState<number>(0);
  const [historyMovements, setHistoryMovements] = useState<HistoryMovement[]>(
    []
  );
  const [movements, setMovements] = useState<Movement[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [rangesPage, setRangesPage] = useState<number>(1);
  const [rangesRowsPerPage, setRangesRowsPerPage] = useState<number>(10);
  const [isValidatingRange, setIsValidatingRange] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: partnerOptionsData, actions: partnerOptionsActions } =
    useGetPartnerOptionsForOperationModal({
      societyId: actualSociety?.['_id'],
      includeSharesCountFirst: true,
      onlyActiveFirst: true,
      isSingleSelect: true,
    });

  const { data: holdingClassOptionsData, actions: holdingClassOptionsActions } =
    useGetHoldingClassOptionsForOperationModal({
      societyId: actualSociety?._id,
      includeShares: true,
    });

  const { lastOperationDate, formattedLastOperationDate } =
    useGetLastOperationDate(actualSociety?._id).data;

  const handleChangeRangesPage = (page: number) => {
    setRangesPage(page);
  };

  const {
    data: validRangesData,
    actions: validRangesActions,
    isLoading: isLoadingAvailableRanges,
  } = useGetAvailableRanges({
    partnerId: selectedPartner,
    holdingClassId: holdingClassOptionsData.selectedHoldingClass,
    page: rangesPage,
    rowsPerPage: rangesRowsPerPage,
    onPageChange: handleChangeRangesPage,
  });

  const handleResetValues = (isHoldingClassSelected: boolean = true) => {
    validRangesActions.handleResetValues();
    partnerOptionsActions.handleUpdatePartnerOptionShares({
      partnerId: selectedPartner,
      sharesCount: unsavedShares,
      isSelectedOptionFirst: true,
    });

    holdingClassOptionsActions.handleUpdateHoldingClasssOptionShares(
      holdingClassOptionsData.selectedHoldingClass,
      unsavedShares,
      isHoldingClassSelected
    );
    setRemainingShares((prev) => prev + unsavedShares);
    setUnsavedShares(0);
    setShareFrom(0);
    setShareTo(0);
    setSharesToDecrease(0);
  };

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeDate = (event: ChangeEvent<HTMLInputElement>) => {
    setDate(event.target.value);
  };

  const handleChangeTotalSharesToDecrease = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const value = cleanValue(event.target.value);
    setTotalSharesToDecrease(value);
    setRemainingShares(value);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFile = (file: File) => {
    setFile(file);
  };

  const onChangeNotaryName = (value: string) => {
    setNotaryName(value);
  };

  const onChangeNotaryProtocol = (value: string) => {
    setNotaryProtocol(value);
  };

  const onChangeNotaryCity = (value: string) => {
    setNotaryCity(value);
  };

  const handleChangePartner = (selectedOption: SelectorOption) => {
    if (validRangesData.validatedRanges.length > 0) {
      asyncActionSwal({
        swalTitle: t('ValidatedRangesWillBeLostWhenChangingPartner'),
        swalElementName: '',
        swalInfoText: '',
        swalConfirmButtonText: t('Continue'),
        callback: () => {
          handleResetValues();
          setSelectedPartner(selectedOption?.value || '');
          partnerOptionsActions.handleChangeSelectedPartnerFirstOption(
            selectedOption
          );
        },
      });
    } else {
      handleResetValues();
      setSelectedPartner(selectedOption?.value || '');
      partnerOptionsActions.handleChangeSelectedPartnerFirstOption(
        selectedOption
      );
    }
  };

  const handleChangeHoldingClass = (selectedOption: SelectorOption) => {
    if (validRangesData.validatedRanges.length > 0) {
      asyncActionSwal({
        swalTitle: t('ValidatedRangesWillBeLostWhenChangingClass'),
        swalElementName: '',
        swalInfoText: '',
        swalConfirmButtonText: t('Continue'),
        callback: () => {
          handleResetValues(false);
          holdingClassOptionsActions.handleClassOptionChange(selectedOption);
        },
      });
    } else {
      handleResetValues(false);
      holdingClassOptionsActions.handleClassOptionChange(selectedOption);
    }
  };

  const handleValidateRange = async () => {
    try {
      setIsValidatingRange(true);
      const range = {
        from: shareFrom,
        to: shareTo,
        partner: selectedPartner,
        shareClass: holdingClassOptionsData.selectedHoldingClass,
      };

      const isValidRange = await validRangesActions.handleValidateRange(range);

      setIsValidatingRange(false);

      if (!isValidRange) return;

      const newShares = -(shareTo - shareFrom + 1);

      partnerOptionsActions.handleUpdatePartnerOptionShares({
        partnerId: selectedPartner,
        sharesCount: newShares,
        isSelectedOptionFirst: true,
      });

      holdingClassOptionsActions.handleUpdateHoldingClasssOptionShares(
        holdingClassOptionsData.selectedHoldingClass,
        newShares,
        true
      );

      setRemainingShares((prev) => prev - sharesToDecrease);
      setUnsavedShares((prev) => prev + sharesToDecrease);
      setSharesToDecrease(0);
      setShareFrom(0);
      setShareTo(0);
    } catch (error) {
      setIsValidatingRange(false);
    }
  };

  const updateSharesToDecrease = (shareFrom: number, shareTo: number) => {
    if (shareFrom < shareTo) {
      setSharesToDecrease(shareTo - shareFrom + 1);
    } else {
      setSharesToDecrease(0);
    }
  };

  const handleChangeShareFrom = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const value = cleanValue(event.target.value);
    setShareFrom(value);

    updateSharesToDecrease(value, shareTo);
  };

  const handleChangeShareTo = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const value = cleanValue(event.target.value);
    setShareTo(value);

    updateSharesToDecrease(shareFrom, value);
  };

  const handleClickRange = (range: Range) => {
    setShareFrom(range.from);
    setShareTo(range.to);

    updateSharesToDecrease(range.from, range.to);
  };

  const handleChangeSharePrice = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const cleanedValue = cleanValue(event.target.value);
    setSharePrice(cleanedValue);
  };

  const handleChangeHasCustomSharePrice = () => {
    setHasCustomSharePrice((prev) => !prev);
    setSharePrice(0);
  };

  const handleChangeSharesToDecrease = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    setSharesToDecrease(cleanValue(event.target.value));
  };

  const handleChangeRangesRowsPerPage = (rowsPerPage: number) => {
    setRangesRowsPerPage(rowsPerPage);
  };

  const handleSaveMovement = () => {
    const rangesToAdd: Range[] = validRangesData.generatedRanges;
    const rangesToRemove: Range[] = validRangesData.removedRanges;

    const movements: Movement[] = getMovements({
      rangesToAdd,
      rangesToRemove,
      partnerId: selectedPartner,
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
    });
    setMovements((prev: Movement[]) => [...prev, ...movements]);

    const historyMovements: HistoryMovement[] = getHistoryMovements({
      validatedRanges: validRangesData.validatedRanges,
      holdingClassName:
        holdingClassOptionsData.selectedHoldingClassOption?.label
          .split('|')[0]
          .trim() || '',
      partnerName:
        partnerOptionsData.selectedPartnerFirstOption?.label
          .split('|')[1]
          .trim() || '',
      sharePrice,
    });
    setHistoryMovements((prev: HistoryMovement[]) => [
      ...historyMovements,
      ...prev,
    ]);

    const transactions: Transaction[] = getTransactions({
      validatedRanges: validRangesData.validatedRanges,
      partnerId: selectedPartner,
      holdingClassId: holdingClassOptionsData.selectedHoldingClass,
      sharePrice,
    });
    setTransactions((prev: Transaction[]) => [...prev, ...transactions]);

    validRangesActions.handleSaveMovement();
    setUnsavedShares(0);
    setShareFrom(0);
    setShareTo(0);
    setSharesToDecrease(0);
  };

  const handleSaveCapitalDecrease = async () => {
    if (scriptureView) {
      setScriptureView(false);
    } else {
      try {
        setIsLoading(true);

        const validMovements = getValidMovements(movements);

        let uploadedDocument = null;

        if (file) {
          const newDocument = {
            file,
            date: new Date(date),
            size: file.size,
            name: operationTypes.CONTRIBUTION_FILENAME,
            author: user['_id'],
            society: actualSociety['_id'],
            fileType: fileTypes[file.type as keyof typeof fileTypes],
            category: documentTypes.SCRIPTURES,
            description: t('DocumentLinkedToOperation'),
          };
          uploadedDocument = await store.dispatch(addDocument(newDocument));
        }

        const newOperationDate = getNewOperationDate(lastOperationDate, date);

        const capitalDecreaseData = {
          movements: validMovements,
          transactions,
          date: newOperationDate,
          nominalValue: actualSociety.nominalValue,
          society: actualSociety['_id'],
          user: user['_id'],
          documents: uploadedDocument?.['_id'] ? [uploadedDocument['_id']] : [],
          comments: cleanText(comments),
          notarialRegistration: {
            notaryName,
            notaryCity,
            protocolNr: notaryProtocol,
          },
          operationType: operationTypes.CAPITAL_DECREASE,
        };

        await store.dispatch(addOperation(capitalDecreaseData));
        setIsLoading(false);

        successSwal(t('CapitalDecreaseSuccessfullyCompleted'));
      } catch (error) {
        errorSwal(t('ErrorWhenPerformingCapitalDecrease'));
      } finally {
        handleCloseModal();
      }
    }
  };

  const handleUploadMock = useCallback(() => {
    if (baseDataForCapitalDecrease.partners.length === 0) return;

    const {
      movements: mockMovements,
      historyMovements: mockHistory,
      transactions: mockTransactions,
      totalSharesToDecrease: mockTotal,
    } = buildMockDataFromBaseData(baseDataForCapitalDecrease);

    setMovements(mockMovements);
    setHistoryMovements(mockHistory);
    setTransactions(mockTransactions);
    setTotalSharesToDecrease(mockTotal);
    setRemainingShares(0);
    setScriptureView(false);

    if (!date) {
      setDate(new Date().toISOString().slice(0, 10));
    }

    holdingClassOptionsActions.handleClassOptionChange({
      value: baseDataForCapitalDecrease.shareClassId,
      label: baseDataForCapitalDecrease.holdingClassName,
    });
    setSharePrice(baseDataForCapitalDecrease.sharePrice);
  }, [holdingClassOptionsActions, date]);

  return (
    <CapitalDecreaseModalContext.Provider
      value={{
        data: {
          date,
          totalSharesToDecrease,
          societyActualShares: actualSociety?.sharesCount?.actual || 0,
          comments,
          file,
          notaryName,
          notaryProtocol,
          notaryCity,
          scriptureView,
          remainingShares,
          selectedPartner,
          selectedPartnerOption: partnerOptionsData.selectedPartnerFirstOption,
          partnerOptions: partnerOptionsData.partnerFirstOptions,
          selectedHoldingClass: holdingClassOptionsData.selectedHoldingClass,
          holdingClassOptions: holdingClassOptionsData.holdingClassOptions,
          selectedHoldingClassOption:
            holdingClassOptionsData.selectedHoldingClassOption,
          sharesToDecrease,
          sharesRanges: validRangesData.validatedRanges,
          shareFrom,
          shareTo,
          hasCustomSharePrice,
          sharePrice,
          isValidRange: validRangesData.isValidRange,
          availableRanges: validRangesData.availableRanges,
          lastOperationDate: formattedLastOperationDate,
          historyMovements,
          totalAvailableRanges: validRangesData.totalAvailableRanges,
          rangesPage,
          rangesRowsPerPage,
          isValidatingRange,
          isLoadingAvailableRanges,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeDate,
          handleChangeTotalSharesToDecrease,
          handleChangeComments,
          handleChangeFile,
          onChangeNotaryName,
          onChangeNotaryProtocol,
          onChangeNotaryCity,
          handleChangePartner,
          handleSearchPartnerByTerm: partnerOptionsActions.fetchPartnersByTerm,
          handleChangeHoldingClass,
          handleFetchHoldingClassByTerm:
            holdingClassOptionsActions.handleFetchHoldingClassByTerm,
          handleValidateRange,
          handleChangeShareFrom,
          handleChangeShareTo,
          handleClickRange,
          handleChangeHasCustomSharePrice,
          handleChangeSharePrice,
          handleChangeSharesToDecrease,
          handleChangeRangesPage,
          handleChangeRangesRowsPerPage,
          handleSaveMovement,
          handleSaveCapitalDecrease,
          handleUploadMock,
        },
      }}
    >
      {children}
    </CapitalDecreaseModalContext.Provider>
  );
};

export const useCapitalDecreaseModal = () => {
  const context = useContext(CapitalDecreaseModalContext);
  if (!context) {
    throw new Error(
      'useCapitalDecrease must be used within a CapitalDecreaseModalProvider'
    );
  }
  return context;
};
